import React, {useEffect, useState} from 'react';
import {Alert, App, Button, Col, Empty, Input, Modal, Row, Spin, Tabs, Tag} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import {OrderApprovalModalProps} from '../../types/riskControl';
import {RealtimePrice, tradingService} from '../../services/tradingService';
import {riskControlService} from '../../services/riskControlService';
import {usePageTimer} from '../../hooks/usePageTimer';
import './OrderApprovalModal.css';

const {TextArea} = Input;

const OrderApprovalModal: React.FC<OrderApprovalModalProps> = ({
                                                                   order,
                                                                   visible,
                                                                   apiKeyId,
                                                                   onClose,
                                                                   onApprove,
                                                                   onReject
                                                               }) => {
    const {message: messageApi} = App.useApp();
    const [activeTabKey, setActiveTabKey] = useState('order');
    const [rejectReason, setRejectReason] = useState('');
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    // 实时价格相关状态
    const [realtimePrice, setRealtimePrice] = useState<RealtimePrice | null>(null);
    const [priceLoading, setPriceLoading] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState<any | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiLoaded, setAiLoaded] = useState(false);

    // 审核通过
    const handleApprove = async () => {
        if (!order) return;

        setLoading(true);
        try {
            await onApprove(order.orderId);
            setTimeout(() => {
                setLoading(false);
                onClose();
                setRejectReason('');
            }, 500);
        } catch (error) {
            setLoading(false);
        }
    };

    // 审核驳回
    const handleReject = async () => {
        if (!order) return;

        if (!rejectReason.trim()) {
            messageApi.warning('请输入驳回原因');
            return;
        }

        setLoading(true);
        try {
            await onReject(order.orderId, rejectReason);
            setTimeout(() => {
                setLoading(false);
                onClose();
                setRejectReason('');
                setRejectModalVisible(false);
            }, 500);
        } catch (error) {
            setLoading(false);
        }
    };

    // 打开驳回弹窗
    const handleRejectClick = () => {
        setRejectModalVisible(true);
    };

    // 关闭驳回弹窗
    const handleRejectModalCancel = () => {
        setRejectModalVisible(false);
        setRejectReason('');
    };

    // 获取实时价格
    const fetchRealtimePrice = async (symbol: string) => {
        try {
            setPriceLoading(true);
            // console.log('正在获取实时价格:', symbol); // 添加调试日志
            const response = await tradingService.getRealtimePrice(symbol, apiKeyId || 0);
            // console.log('实时价格原始响应:', response); // 添加调试日志

            // 处理新的 ApiResponse<RealtimePrice> 结构
            if (response.success && response.data) {
                const priceData = response.data;
                // console.log('处理后的价格数据:', priceData); // 添加调试日志
                setRealtimePrice(priceData);
            } else {
                // console.warn('实时价格数据格式不正确:', response);
                setRealtimePrice(null);
            }
        } catch (error) {
            // console.error('获取实时价格失败:', error);
            setRealtimePrice(null);
        } finally {
            setPriceLoading(false);
        }
    };

    const fetchAiAnalysis = async (force = false) => {
        if (!order?.orderId || (!force && aiLoading)) return;
        setAiLoading(true);
        try {
            const response = await riskControlService.getAiAnalysis(order.orderId);
            if (response.success) {
                setAiAnalysis(response.data || null);
                setAiLoaded(true);
            } else {
                setAiAnalysis({
                    status: 'FAILED',
                    errorMessage: response.message || '获取AI分析失败'
                });
                setAiLoaded(true);
            }
        } catch (error: any) {
            setAiAnalysis({
                status: 'FAILED',
                errorMessage: error?.response?.data?.message || '获取AI分析失败'
            });
            setAiLoaded(true);
        } finally {
            setAiLoading(false);
        }
    };

    // 格式化止盈止损详情
    const formatStopLossDetail = (price: string | undefined, currentPrice: number | undefined, side: string, type: 'profit' | 'loss') => {
        if (!price || !currentPrice || !order) return `${price} (--)`;

        const targetPrice = parseFloat(price);
        const percentage = ((targetPrice - currentPrice) / currentPrice * 100) * (side === 'BUY' ? 1 : -1);
        const amount = parseFloat(order.quantity || '0');
        const expectedValue = Math.abs(targetPrice - currentPrice) * amount;

        const sign = percentage >= 0 ? '+' : '';
        const percentageColor = percentage >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)';
        const percentageText = `${sign}${percentage.toFixed(2)}%`;
        const expectedText = type === 'profit' ? `预期盈利: ${expectedValue.toFixed(2)}` : `预期亏损: ${expectedValue.toFixed(2)}`;

        return (
            <span className="stop-loss-detail">
                {price}
                {' ('}
                <span style={{color: percentageColor}} className="percentage-value">
                    {percentageText}
                </span>
                {' | '}
                <span className="expected-value">{expectedText}</span>
                {')'}
            </span>
        );
    };

    // 格式化价格显示 - 去除末尾多余的零，保持最多6位有效小数
    const formatPrice = (price: number) => {
        return parseFloat(price.toFixed(6)).toString();
    };

    // 使用定时器实现5秒更新实时价格
    const {isActive} = usePageTimer(
        async () => {
            if (order?.symbol && visible) {
                await fetchRealtimePrice(order.symbol);
            }
        },
        {
            interval: 5000,
            enabled: visible && !!order?.symbol,
            persistKey: 'risk-modal-realtime-price'
        }
    );

    // 弹窗打开时立即获取一次价格
    useEffect(() => {
        if (visible && order?.symbol) {
            fetchRealtimePrice(order.symbol);
        } else {
            // 弹窗关闭时清空价格数据
            setRealtimePrice(null);
        }
    }, [visible, order?.symbol]);

    useEffect(() => {
        if (!visible) {
            setActiveTabKey('order');
            setAiAnalysis(null);
            setAiLoaded(false);
            return;
        }
        if (activeTabKey === 'ai' && order?.orderId && !aiLoaded) {
            fetchAiAnalysis();
        }
    }, [visible, activeTabKey, order?.orderId, aiLoaded]);

    // 获取状态图标
    const getStatusIcon = (auditStatus: string) => {
        switch (auditStatus) {
            case 'PENDING':
                return <ClockCircleOutlined style={{color: 'var(--pt-primary)'}}/>;
            case 'APPROVED':
                return <CheckCircleOutlined style={{color: 'var(--pt-bull)'}}/>;
            case 'REJECTED':
                return <CloseCircleOutlined style={{color: 'var(--pt-bear)'}}/>;
            default:
                return <ExclamationCircleOutlined style={{color: 'var(--pt-amber)'}}/>;
        }
    };

    if (!order) return null;

    const getAiDecisionTag = () => {
        if (!aiAnalysis) {
            return <Tag color="default">待加载</Tag>;
        }
        if (aiAnalysis.status === 'PROCESSING') {
            return <Tag color="processing">分析中</Tag>;
        }
        if (aiAnalysis.status === 'FAILED') {
            return <Tag color="error">分析失败</Tag>;
        }
        if (aiAnalysis.decision === 'APPROVE') {
            return <Tag color="success">建议通过</Tag>;
        }
        if (aiAnalysis.decision === 'REJECT') {
            return <Tag color="error">建议驳回</Tag>;
        }
        return <Tag color="default">{aiAnalysis.status || '未知'}</Tag>;
    };

    const renderAiAnalysis = () => {
        if (aiLoading && !aiLoaded) {
            return (
                <div style={{padding: '40px 0', textAlign: 'center'}}>
                    <Spin tip="AI正在分析本次交易风险..."/>
                </div>
            );
        }

        if (!aiAnalysis) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无AI分析结果"/>;
        }

        if (aiAnalysis.status === 'FAILED') {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="AI分析失败"
                    description={aiAnalysis.errorMessage || '暂时无法获取AI风控分析'}
                />
            );
        }

        const riskPoints = aiAnalysis.riskPoints || [];
        const suggestions = aiAnalysis.suggestions || [];

        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                <div style={{
                    background: 'rgba(7, 10, 18, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgb(64, 64, 64)'
                }}>
                    <Row justify="space-between" align="middle" style={{marginBottom: 12}}>
                        <Col>
                            <span style={{color: '#a0a0a0'}}><strong>AI结论：</strong></span>
                        </Col>
                        <Col>{getAiDecisionTag()}</Col>
                    </Row>
                    <div style={{color: '#ffffff', marginBottom: 12}}>
                        {aiAnalysis.summary || '暂无摘要'}
                    </div>
                    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        {aiAnalysis.modelName && <Tag color="blue">{aiAnalysis.modelName}</Tag>}
                        {aiAnalysis.completedTime && (
                            <Tag color="default">{riskControlService.formatDateTime(aiAnalysis.completedTime)}</Tag>
                        )}
                    </div>
                </div>

                <div style={{
                    background: 'rgba(7, 10, 18, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgb(64, 64, 64)'
                }}>
                    <div style={{color: '#a0a0a0', marginBottom: 8}}><strong>主要风险点</strong></div>
                    {riskPoints.length > 0 ? riskPoints.map((item: string, index: number) => (
                        <div key={`risk-${index}`} style={{color: '#ffffff', marginBottom: 6}}>
                            {index + 1}. {item}
                        </div>
                    )) : <div style={{color: '#8c8c8c'}}>未识别到明显风险点</div>}
                </div>

                <div style={{
                    background: 'rgba(7, 10, 18, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgb(64, 64, 64)'
                }}>
                    <div style={{color: '#a0a0a0', marginBottom: 8}}><strong>风控建议</strong></div>
                    {suggestions.length > 0 ? suggestions.map((item: string, index: number) => (
                        <div key={`suggestion-${index}`} style={{color: '#ffffff', marginBottom: 6}}>
                            {index + 1}. {item}
                        </div>
                    )) : <div style={{color: '#8c8c8c'}}>暂无额外建议</div>}
                </div>

                {(aiAnalysis.recommendedTakeProfit || aiAnalysis.recommendedStopLoss) && (
                    <div style={{
                        background: 'rgba(7, 10, 18, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgb(64, 64, 64)'
                    }}>
                        {aiAnalysis.recommendedTakeProfit && (
                            <div style={{color: '#ffffff', marginBottom: 8}}>
                                建议止盈：{aiAnalysis.recommendedTakeProfit}
                            </div>
                        )}
                        {aiAnalysis.recommendedStopLoss && (
                            <div style={{color: '#ffffff'}}>
                                建议止损：{aiAnalysis.recommendedStopLoss}
                            </div>
                        )}
                    </div>
                )}

                <Button onClick={() => fetchAiAnalysis(true)} loading={aiLoading}>
                    刷新AI分析
                </Button>
            </div>
        );
    };

    const orderInfoContent = (
        <div>
            <div
                style={{
                    background: 'rgba(7, 10, 18, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid rgb(64, 64, 64)'
                }}
            >
                {/* 新增：实时价格显示 */}
                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>实时价格：</strong></span>
                    </Col>
                    <Col>
                                <span
                                    style={{color: priceLoading ? 'var(--pt-amber)' : 'var(--pt-bull)'}}
                                    className={`realtime-price ${priceLoading ? 'realtime-price-updating' : ''}`}
                                >
                                    {priceLoading ? '加载中...' : realtimePrice ? formatPrice(realtimePrice.lastPrice) : '--'}
                                </span>
                    </Col>
                </Row>

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>订单来源：</strong></span>
                    </Col>
                    <Col>
                        <Tag color={order.orderSource === 'ai' ? 'purple' : 'blue'}>
                            {order.orderSource === 'ai' ? 'AI下单' : '用户下单'}
                        </Tag>
                    </Col>
                </Row>

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>交易对：</strong></span>
                    </Col>
                    <Col>
                        <span style={{color: 'rgb(255, 255, 255)'}}>{order.symbol}</span>
                    </Col>
                </Row>

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>订单类型：</strong></span>
                    </Col>
                    <Col>
                        <Tag color={order.orderType === 'LIMIT' ? 'blue' : 'green'}>
                            {order.orderType ? riskControlService.getOrderTypeText(order.orderType) : '-'}
                        </Tag>
                    </Col>
                </Row>

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>方向：</strong></span>
                    </Col>
                    <Col>
                        <Tag color={order.side === 'BUY' ? 'green' : 'red'}>
                            {order.side ? riskControlService.getOrderSideText(order.side) : '-'}
                        </Tag>
                    </Col>
                </Row>

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>数量：</strong></span>
                    </Col>
                    <Col>
                <span style={{color: 'rgb(255, 255, 255)'}}>
                  {riskControlService.formatQuantity(order.quantity)}
                </span>
                    </Col>
                </Row>

                {order.price && (
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>价格：</strong></span>
                        </Col>
                        <Col>
                  <span style={{color: 'rgb(255, 255, 255)'}}>
                    {riskControlService.formatPrice(order.price)}
                  </span>
                        </Col>
                    </Row>
                )}

                {/* 拆分：止盈 */}
                {order.takeProfitPrice && (
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>止盈：</strong></span>
                        </Col>
                        <Col>
                                    <span style={{color: 'rgb(255, 255, 255)'}}>
                                        {formatStopLossDetail(order.takeProfitPrice, realtimePrice?.lastPrice, order.side || '', 'profit')}
                                    </span>
                        </Col>
                    </Row>
                )}

                {/* 拆分：止损 */}
                {order.stopLossPrice && (
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>止损：</strong></span>
                        </Col>
                        <Col>
                                    <span style={{color: 'rgb(255, 255, 255)'}}>
                                        {formatStopLossDetail(order.stopLossPrice, realtimePrice?.lastPrice, order.side || '', 'loss')}
                                    </span>
                        </Col>
                    </Row>
                )}

                {/* 暂时隐藏风控等级显示
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col>
                                <span style={{color: 'rgb(160, 160, 160)'}}><strong>风控等级：</strong></span>
                            </Col>
                            <Col>
                                <Tag color={riskControlService.getRiskLevelColor(order.riskLevel)}>
                                    {riskControlService.getRiskLevelText(order.riskLevel)}
                                </Tag>
                            </Col>
                        </Row>
                        */}

                <Row justify="space-between" style={{marginBottom: '8px'}}>
                    <Col>
                        <span style={{color: 'rgb(160, 160, 160)'}}><strong>创建时间：</strong></span>
                    </Col>
                    <Col>
                <span style={{color: 'rgb(255, 255, 255)'}}>
                  {riskControlService.formatDateTime(order.createTime)}
                </span>
                    </Col>
                </Row>

                {order.auditTime && (
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>审核时间：</strong></span>
                        </Col>
                        <Col>
                  <span style={{color: 'rgb(255, 255, 255)'}}>
                    {riskControlService.formatDateTime(order.auditTime)}
                  </span>
                        </Col>
                    </Row>
                )}

                {/* 预估总占用资金 - 新增区块 */}
                {order.estimatedTotalCapital && (
                    <Row gutter={[16, 16]} style={{marginTop: '16px', marginBottom: '8px'}}>
                        <Col span={24}>
                            <div style={{
                                background: 'rgba(7, 10, 18, 0.3)',
                                padding: '16px',
                                borderRadius: '8px',
                                border: '1px solid rgb(64, 64, 64)'
                            }}>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                                <span style={{color: 'rgb(160, 160, 160)', fontSize: '14px'}}>
                                                    <strong>预估总占用资金:</strong>
                                                </span>
                                    </Col>
                                    <Col>
                                                <span style={{
                                                    color: 'var(--pt-bear)',
                                                    fontWeight: 'bold',
                                                    fontSize: '18px',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {parseFloat(order.estimatedTotalCapital).toFixed(2)}
                                                </span>
                                    </Col>
                                </Row>
                                <div style={{
                                    marginTop: '8px',
                                    fontSize: '12px',
                                    color: 'rgb(128, 128, 128)'
                                }}>
                                    包含保证金 + 开仓手续费 + 平仓手续费
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {order.auditor && (
                    <Row justify="space-between" style={{marginBottom: '8px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>审核人：</strong></span>
                        </Col>
                        <Col>
                            <span style={{color: 'rgb(255, 255, 255)'}}>{order.auditor}</span>
                        </Col>
                    </Row>
                )}

                {order.rejectionReason && (
                    <Row justify="space-between" style={{marginBottom: '0px'}}>
                        <Col>
                            <span style={{color: 'rgb(160, 160, 160)'}}><strong>驳回原因：</strong></span>
                        </Col>
                        <Col>
                            <span style={{color: 'rgb(255, 255, 255)'}}>{order.rejectionReason}</span>
                        </Col>
                    </Row>
                )}
            </div>

            {order.auditStatus === 'PENDING' && (
                <div className="approval-actions">
                    <div className="action-tips">
                        <ExclamationCircleOutlined style={{color: 'var(--pt-amber)', marginRight: '8px'}}/>
                        <span>请仔细核对订单信息后进行审核操作</span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            <Modal
                title={
                    <div className="modal-title">
                        {getStatusIcon(order.auditStatus || '')}
                        <span>订单风控审核</span>
                    </div>
                }
                open={visible}
                onCancel={onClose}
                footer={[
                    <Button
                        key="reject"
                        danger
                        onClick={handleRejectClick}
                        disabled={loading || order.auditStatus !== 'PENDING'}
                    >
                        驳回
                    </Button>,
                    <Button
                        key="approve"
                        type="primary"
                        loading={loading}
                        onClick={handleApprove}
                        disabled={order.auditStatus !== 'PENDING'}
                    >
                        通过
                    </Button>,
                ]}
                width={680}
                className="order-approval-modal"
            >
                <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={[
                        {key: 'order', label: '订单信息', children: orderInfoContent},
                        {key: 'ai', label: 'AI分析', children: renderAiAnalysis()}
                    ]}
                />
            </Modal>

            {/* 驳回原因弹窗 */}
            <Modal
                title="驳回订单"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={handleRejectModalCancel}
                okText="确认驳回"
                cancelText="取消"
                confirmLoading={loading}
                className="reject-modal"
                width={400}
            >
                <div className="reject-form">
                    <div className="reject-tip">
                        <ExclamationCircleOutlined style={{color: 'var(--pt-amber)', marginRight: '8px'}}/>
                        <span>请输入驳回原因，以便后续分析和改进</span>
                    </div>
                    <TextArea
                        placeholder="请输入驳回原因..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={4}
                        maxLength={200}
                        showCount
                        className="reject-textarea"
                    />
                </div>
            </Modal>
        </>
    );
};

export default OrderApprovalModal;
